import { useCallback, useEffect, useRef, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { WorkerChip } from './useWorkersByIds';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

interface RecipientsResponse {
  success: boolean;
  data: WorkerChip[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

// Browsing and searching a broadcast's recipients both go through one graphy
// endpoint, which resolves the recipient set in Supabase and then searches
// and paginates INSIDE it.
//
// The first implementation searched all workers, took the first page, and
// intersected that with the recipients client-side. On prod that returned
// nothing: the worker search caps at 100 rows, "john" matches 879 of 52k
// workers, and one broadcast's recipients are a fraction of a percent of the
// table, so the two sets almost never overlapped. Scoping the search
// server-side is the fix — see GRAVY-10334.
export function useBroadcastRecipientList(
  broadcastId: string | null,
  search: string,
  enabled: boolean,
) {
  const [recipients, setRecipients] = useState<WorkerChip[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);
  // Bumped on every input change (and on unmount) so a response for a
  // superseded broadcast/search can never clobber current state.
  const requestIdRef = useRef(0);

  const trimmedSearch = search.trim();

  const fetchPage = useCallback(
    async (page: number, append: boolean, requestId: number) => {
      if (!broadcastId || !enabled) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axiosRubyClient.get<RecipientsResponse>(
          `/api/admin/broadcasts/${broadcastId}/recipients`,
          {
            params: {
              search: trimmedSearch || undefined,
              page,
              per_page: PAGE_SIZE,
            },
          },
        );
        if (requestIdRef.current !== requestId) return; // stale — superseded
        const rows = res.data.data ?? [];
        const pagination = res.data.pagination;
        pageRef.current = page;
        setTotal(pagination?.total ?? null);
        setRecipients((prev) => (append ? [...prev, ...rows] : rows));
        setHasMore(
          pagination != null && pagination.page < pagination.total_pages,
        );
      } catch (err: unknown) {
        if (requestIdRef.current !== requestId) return; // stale — superseded
        captureSupabaseError(err, { action: 'broadcast_recipient_list' });
        setError('Unable to load recipients right now.');
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [broadcastId, enabled, trimmedSearch],
  );

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    pageRef.current = 1;
    setRecipients([]);
    setTotal(null);
    setHasMore(false);

    if (!broadcastId || !enabled) {
      setLoading(false);
      return;
    }

    // Set synchronously, before the debounce, so a consumer never observes
    // loading=false alongside emptied results and renders "no matches".
    setLoading(true);

    const handle = setTimeout(() => {
      void fetchPage(1, false, requestId);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(handle);
      requestIdRef.current += 1;
    };
  }, [broadcastId, enabled, trimmedSearch, fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    void fetchPage(pageRef.current + 1, true, requestIdRef.current);
  }, [fetchPage, hasMore, loading]);

  return { recipients, total, loading, error, hasMore, loadMore };
}

export default useBroadcastRecipientList;
